import { useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '@pazportal/ui';
import { useActiveTrip } from '../hooks/useActiveTrip';
import {
  useAddOpenItem,
  useCloseOpenItem,
  useTripBrief,
  type BriefOpenItem,
  type OpenItemPriority,
  type TripBrief,
} from '../hooks/useTripBrief';
import { TripPicker } from '../components/TripPicker';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { useToast } from '../components/Toaster';
import { formatDate, formatSolutionType, humanize, pluralize, timeAgo } from '../lib/formatters';
import { hrefWithTrip } from '../lib/tripQuery';

const PRIORITY_TONE: Record<OpenItemPriority, string> = {
  high: 'bg-notification-soft text-notification',
  medium: 'bg-warning-soft text-warning',
  low: 'bg-surface-3 text-text-muted',
};

/**
 * Before you go in: what is still open at this property, what happened last
 * time, and what else is waiting. Everything a crew would otherwise have to
 * dig out of old reports, Zoho and memory.
 */
export function BriefPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const brief = useTripBrief(trip?.id);

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-text">Pre-trip brief</h1>
          <p className="mt-0.5 text-sm text-text-muted">
            {trip.location?.name ?? 'Unknown property'} · <span className="font-mono">{trip.project_number}</span>
          </p>
        </div>
        <StatusBadge status={trip.status ?? 'scheduled'} />
      </div>

      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}

      {brief.isLoading ? (
        <LoadingBlock label="Putting the brief together…" />
      ) : brief.isError || !brief.data ? (
        <ErrorBlock title="The brief could not be loaded" error={brief.error} onRetry={() => void brief.refetch()} />
      ) : (
        <BriefBody brief={brief.data} tripId={trip.id} stale={brief.isRefetchError} />
      )}
    </div>
  );
}

function BriefBody({ brief, tripId, stale }: { brief: TripBrief; tripId: string; stale: boolean }) {
  return (
    <>
      {stale && (
        <p className="rounded-md bg-warning-soft px-3 py-2 text-xs text-warning" role="status">
          Showing the brief from {timeAgo(brief.generated_at)}; it could not be refreshed.
        </p>
      )}

      {brief.run && (
        <section className="card p-4" aria-labelledby="run-heading">
          <h2 id="run-heading" className="card-title mb-1">This run</h2>
          <p className="text-sm text-text-muted mb-3">
            {brief.run.title} · <span className="font-mono">{brief.run.run_number}</span>
          </p>
          <ol className="space-y-1.5">
            {brief.run.stops.map((s) => {
              const here = s.trip_id === tripId;
              return (
                <li
                  key={s.trip_id}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${here ? 'bg-primary-soft ring-1 ring-primary' : 'bg-surface-2'}`}
                  aria-current={here ? 'step' : undefined}
                >
                  <span className="w-5 font-mono text-xs text-text-faint">{s.stop ?? '–'}.</span>
                  <span className="min-w-0 flex-1 truncate font-medium text-text">{s.location_name ?? 'Unknown property'}</span>
                  <span className="text-xs text-text-muted whitespace-nowrap">{s.start_date ? formatDate(s.start_date) : ''}</span>
                  {s.open_items > 0 && <span className="text-xs text-warning whitespace-nowrap">{s.open_items} open</span>}
                  <StatusBadge status={s.status} />
                </li>
              );
            })}
          </ol>
        </section>
      )}

      <OpenItemsCard brief={brief} tripId={tripId} />

      <section className="card p-4" aria-labelledby="last-heading">
        <h2 id="last-heading" className="card-title mb-3">Last visit here</h2>
        {!brief.previous_visit ? (
          <p className="text-sm text-text-muted">No earlier visit recorded. This is the first one in PazPortal.</p>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-text">
              {formatDate(brief.previous_visit.start_date)}
              {brief.previous_visit.end_date && brief.previous_visit.end_date !== brief.previous_visit.start_date
                ? ` → ${formatDate(brief.previous_visit.end_date)}`
                : ''}
              {brief.previous_visit.lead_technician ? ` · ${brief.previous_visit.lead_technician}` : ''}
              <span className="text-text-faint"> · {brief.previous_visit.project_number}</span>
            </p>
            {brief.previous_visit.scope && <p className="text-text-muted whitespace-pre-wrap">{brief.previous_visit.scope}</p>}
            {brief.previous_visit.imported && !brief.previous_visit.last_report && (
              <p className="text-xs text-text-faint">Imported from Zoho history — no daily report on file.</p>
            )}
            {brief.previous_visit.last_report && (
              <dl className="space-y-2 rounded-md bg-surface-2 p-3">
                <LastReportLine label="Work completed" value={brief.previous_visit.last_report.work_completed} />
                <LastReportLine label="Planned next" value={brief.previous_visit.last_report.tomorrow_plan} />
                <LastReportLine label="Punch list" value={brief.previous_visit.last_report.punch_list} />
                <LastReportLine label="Internal notes" value={brief.previous_visit.last_report.internal_notes} />
                <p className="text-[11px] text-text-faint">From the report of {formatDate(brief.previous_visit.last_report.report_date)}</p>
              </dl>
            )}
          </div>
        )}
        <p className="mt-3 text-xs text-text-faint">{pluralize(brief.visit_count, 'completed visit')} to this property on record.</p>
      </section>

      <section className="card p-4" aria-labelledby="tickets-heading">
        <h2 id="tickets-heading" className="card-title mb-3">Open tickets ({brief.open_tickets.length})</h2>
        {brief.open_tickets.length === 0 ? (
          <p className="text-sm text-text-muted">No open tickets at this property.</p>
        ) : (
          <ul className="space-y-2">
            {brief.open_tickets.map((t) => (
              <li key={t.id} className="flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-text break-words">{t.title}</p>
                  <p className="text-xs text-text-muted">
                    <span className="font-mono">{t.ticket_number}</span> · opened {formatDate(t.created_at)}
                    {t.priority ? ` · ${humanize(t.priority)} priority` : ''}
                  </p>
                </div>
                <StatusBadge status={t.status} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {brief.recent_leads.length > 0 && (
        <section className="card p-4" aria-labelledby="leads-heading">
          <h2 id="leads-heading" className="card-title mb-3">Open leads</h2>
          <ul className="space-y-2">
            {brief.recent_leads.map((l) => (
              <li key={l.id} className="text-sm">
                <p className="text-text break-words">{l.observation}</p>
                <p className="text-xs text-text-muted">
                  {l.solution_type ? formatSolutionType(l.solution_type) : 'General'} · {formatDate(l.created_at)}
                </p>
              </li>
            ))}
          </ul>
          <Link to={hrefWithTrip('/intel', tripId)} className="mt-3 inline-block text-xs font-medium text-primary hover:underline">
            All site intel
          </Link>
        </section>
      )}
    </>
  );
}

function LastReportLine({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-text whitespace-pre-wrap break-words">{value}</dd>
    </div>
  );
}

function OpenItemsCard({ brief, tripId }: { brief: TripBrief; tripId: string }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="card p-4" aria-labelledby="open-heading">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id="open-heading" className="card-title">Still open here ({brief.open_items.length})</h2>
        {!adding && (
          <button type="button" onClick={() => setAdding(true)} className="btn-secondary">
            Add item
          </button>
        )}
      </div>
      {adding && <AddItemForm locationId={brief.location_id} tripId={tripId} onDone={() => setAdding(false)} />}
      {brief.open_items.length === 0 ? (
        <p className="text-sm text-text-muted">Nothing left open from earlier visits.</p>
      ) : (
        <ul className="space-y-2">
          {brief.open_items.map((i) => (
            <OpenItemRow key={i.id} item={i} tripId={tripId} />
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-text-faint">
        Anything you leave undone today: add it here so the next visit picks it up.
      </p>
    </section>
  );
}

function AddItemForm({ locationId, tripId, onDone }: { locationId: string; tripId: string; onDone: () => void }) {
  const add = useAddOpenItem(tripId);
  const { notify } = useToast();
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [priority, setPriority] = useState<OpenItemPriority>('medium');

  return (
    <form
      className="mb-3 space-y-3 rounded-md bg-surface-2 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        add.mutate(
          { locationId, title: title.trim(), detail: detail.trim(), priority },
          {
            onSuccess: () => {
              notify('Open item added. It will show on the next visit here.', 'success');
              onDone();
            },
          },
        );
      }}
    >
      <div>
        <label htmlFor="oi-title" className="input-label">What is left to do?</label>
        <input id="oi-title" value={title} onChange={(e) => setTitle(e.target.value)} className="input-field" autoComplete="off" />
      </div>
      <div>
        <label htmlFor="oi-detail" className="input-label">Details <span className="font-normal text-text-faint">(optional)</span></label>
        <textarea id="oi-detail" value={detail} onChange={(e) => setDetail(e.target.value)} rows={2} className="input-field" />
      </div>
      <fieldset className="flex gap-4 text-sm text-text">
        <legend className="input-label">Priority</legend>
        {(['high', 'medium', 'low'] as const).map((p) => (
          <label key={p} className="flex min-h-[44px] items-center gap-2">
            <input type="radio" name="oi-priority" checked={priority === p} onChange={() => setPriority(p)} />
            {humanize(p)}
          </label>
        ))}
      </fieldset>
      {add.error && <p role="alert" className="text-xs text-notification">{(add.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={!title.trim() || add.isPending} className="btn-primary">
          {add.isPending ? 'Adding…' : 'Add item'}
        </button>
      </div>
    </form>
  );
}

function OpenItemRow({ item, tripId }: { item: BriefOpenItem; tripId: string }) {
  const close = useCloseOpenItem(tripId);
  const { notify } = useToast();
  const [closing, setClosing] = useState<null | 'resolved' | 'wont_fix'>(null);
  const [note, setNote] = useState('');

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text break-words">{item.title}</p>
          {item.detail && <p className="mt-0.5 text-xs text-text-muted whitespace-pre-wrap break-words">{item.detail}</p>}
          <p className="mt-1 text-[11px] text-text-faint">
            Opened {formatDate(item.opened_at)}
            {item.opened_by ? ` by ${item.opened_by}` : ''}
            {item.opened_project_number ? ` · ${item.opened_project_number}` : ''}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${PRIORITY_TONE[item.priority]}`}>{humanize(item.priority)}</span>
      </div>
      {closing ? (
        <form
          className="mt-2 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!note.trim()) return;
            close.mutate(
              { id: item.id, status: closing, resolution: note.trim() },
              { onSuccess: () => notify(closing === 'resolved' ? 'Marked resolved.' : "Closed as won't fix.", 'success') },
            );
          }}
        >
          <label htmlFor={`note-${item.id}`} className="input-label">
            {closing === 'resolved' ? 'What was done?' : "Why won't this be done?"}
          </label>
          <input id={`note-${item.id}`} value={note} onChange={(e) => setNote(e.target.value)} className="input-field" autoFocus />
          {close.error && <p role="alert" className="text-xs text-notification">{(close.error as Error).message}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setClosing(null)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={!note.trim() || close.isPending} className="btn-primary">Save</button>
          </div>
        </form>
      ) : (
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={() => setClosing('resolved')} className="btn-secondary">Resolved</button>
          <button type="button" onClick={() => setClosing('wont_fix')} className="btn-secondary">Won't fix</button>
        </div>
      )}
    </li>
  );
}
